import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FriendsContext, Friend, FriendRequest, MessageNotification } from '../Providers/FriendsContext';
import { AuthContext } from '../Providers/AuthContext';
import { GameInvite, FriendsService } from '../../services/firebase/friendsService';
import { UserDataService, FirebaseUserData } from '../../services/firebase/userDataService';
import { TimeControlType } from '../../services/MMR/types';
import { MMRService } from '../../services/MMR/MMRService';
import TimeControlBadge from '../TimeControlBadge/TimeControlBadge';
import ZodiacEngineService from '../ZodiacEngineService';
import { SandboxSessionService } from '../../services/firebase/sandboxSessionService';
import { updateGamesDocument, queryActiveGamesForUser } from '../../services/firebase/firebase';
import styles from './FriendsSidebar.module.css';

type Tab = 'friends' | 'requests' | 'add';

function getPieceImage(zodiacResult: string): string {
  if (!zodiacResult) return '';
  const [variant, ...nameParts] = zodiacResult.split(' ');
  const pieceName = nameParts.join(' ');
  return `/Icons/${pieceName}-${variant.toLowerCase()}.png`;
}

// ─── Main Sidebar ───────────────────────────────────────────────────────────

export default function FriendsSidebar() {
  const {
    friends, pendingReceived, pendingSent, incomingInvites, unreadCount,
    unreadByConversationId,
    onlineNotifications, messageNotifications,
    dismissOnlineNotification, dismissMessageNotification,
    acceptRequest, declineRequest, removeFriend, sendRequest, sendGameInvite,
    activeConversation, activeConversationFriend, messages,
    openConversation, closeConversation, sendMessage,
    isSidebarOpen, openSidebar, closeSidebar,
  } = useContext(FriendsContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('friends');
  const [profileFriend, setProfileFriend] = useState<Friend | null>(null);
  const [playFriend, setPlayFriend] = useState<Friend | null>(null);
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{ gameId: string; friendName: string; type: 'casual' | 'sandbox' } | null>(null);
  const [declineNotification, setDeclineNotification] = useState<{ friendName: string } | null>(null);
  const [inMatchNotification, setInMatchNotification] = useState<{ friendName: string } | null>(null);
  const inviteUnsubRef = useRef<(() => void) | null>(null);

  // Clean up invite subscription on unmount
  useEffect(() => {
    return () => {
      if (inviteUnsubRef.current) inviteUnsubRef.current();
    };
  }, []);

  const handlePlayCasual = async (friend: Friend) => {
    if (!user || isCreatingGame) return;
    setIsCreatingGame(true);
    setPlayFriend(null);
    try {
      // Check if friend is already in an active game
      const activeGames = await queryActiveGamesForUser(friend.uid);
      if (activeGames.length > 0) {
        setInMatchNotification({ friendName: friend.username });
        setIsCreatingGame(false);
        return;
      }

      const gameId = await ZodiacEngineService.zodiacEngine.switchToLiveMove(user.uid);
      ZodiacEngineService.zodiacEngine.setMatchmakingMode('casual');
      const engineState = ZodiacEngineService.zodiacEngine.getGameState();
      engineState.timeControlPreset = 'marathon';
      engineState.lightPlayerMatchmakingMode = 'casual';
      engineState.darkPlayerMatchmakingMode = 'casual';

      // Persist casual mode to Firebase so both players see it on join
      await updateGamesDocument(gameId, {
        matchmakingMode: 'casual',
        lightPlayerMatchmakingMode: 'casual',
        darkPlayerMatchmakingMode: 'casual',
        timeControlPreset: 'marathon',
      });

      await sendGameInvite(friend.uid, gameId, 'casual');

      setPendingInvite({ gameId, friendName: friend.username, type: 'casual' });
      closeSidebar();

      // Subscribe — navigate only on accept
      inviteUnsubRef.current = FriendsService.subscribeSentInviteStatus(
        gameId,
        user.uid,
        () => {
          // Declined
          setPendingInvite(null);
          setIsCreatingGame(false);
          setDeclineNotification({ friendName: friend.username });
          if (inviteUnsubRef.current) { inviteUnsubRef.current(); inviteUnsubRef.current = null; }
        },
        () => {
          // Accepted — navigate now
          setPendingInvite(null);
          if (inviteUnsubRef.current) { inviteUnsubRef.current(); inviteUnsubRef.current = null; }
          window.location.href = `/multiplayer?host=${gameId}`;
        }
      );
    } catch {
      setIsCreatingGame(false);
      setDeclineNotification({ friendName: 'Friend' });
    }
  };

  const handlePlaySandbox = async (friend: Friend) => {
    if (!user || isCreatingGame) return;
    setIsCreatingGame(true);
    setPlayFriend(null);
    try {
      // Check if friend is already in an active game
      const activeGames = await queryActiveGamesForUser(friend.uid);
      if (activeGames.length > 0) {
        setInMatchNotification({ friendName: friend.username });
        setIsCreatingGame(false);
        return;
      }

      const sessionId = await SandboxSessionService.createSession(user.uid, friend.uid);
      await FriendsService.deleteInvitesForGame(sessionId, user.uid);
      await sendGameInvite(friend.uid, sessionId, 'sandbox');

      setPendingInvite({ gameId: sessionId, friendName: friend.username, type: 'sandbox' });
      closeSidebar();

      // Subscribe — navigate only on accept
      inviteUnsubRef.current = FriendsService.subscribeSentInviteStatus(
        sessionId,
        user.uid,
        () => {
          // Declined
          setPendingInvite(null);
          setIsCreatingGame(false);
          setDeclineNotification({ friendName: friend.username });
          SandboxSessionService.closeSession(sessionId).catch(() => {});
          if (inviteUnsubRef.current) { inviteUnsubRef.current(); inviteUnsubRef.current = null; }
        },
        () => {
          // Accepted — navigate now
          setPendingInvite(null);
          if (inviteUnsubRef.current) { inviteUnsubRef.current(); inviteUnsubRef.current = null; }
          navigate(`/shared-sandbox/${sessionId}`);
        }
      );
    } catch {
      setIsCreatingGame(false);
      setDeclineNotification({ friendName: 'Friend' });
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isSidebarOpen ? styles.open : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <div className={`${styles.sidebar} ${isSidebarOpen ? styles.open : ''}`}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.headerTitle}>Friends</span>
          <button className={styles.closeButton} onClick={closeSidebar}>
            &#10005;
          </button>
        </div>

        {/* If viewing profile, show profile view */}
        {profileFriend ? (
          <div className={styles.content}>
            <FriendProfileView
              friend={profileFriend}
              onBack={() => setProfileFriend(null)}
              onChat={(uid) => { setProfileFriend(null); openConversation(uid); }}
              onRemove={(friendshipId) => { removeFriend(friendshipId); setProfileFriend(null); }}
              onPlay={(friend) => setPlayFriend(friend)}
            />
          </div>
        ) : activeConversation && activeConversationFriend ? (
          <div className={styles.content}>
            <ChatView
              friend={activeConversationFriend}
              messages={messages}
              onBack={closeConversation}
              onSend={sendMessage}
            />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'friends' ? styles.active : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                Friends
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'requests' ? styles.active : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                Requests
                {pendingReceived.length > 0 && (
                  <span className={styles.badge}>{pendingReceived.length}</span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'add' ? styles.active : ''}`}
                onClick={() => setActiveTab('add')}
              >
                Add
              </button>
            </div>

            {/* Tab Content */}
            <div className={styles.content}>
              {activeTab === 'friends' && (
                <FriendsTab
                  friends={friends}
                  unreadByConversationId={unreadByConversationId}
                  onMessage={openConversation}
                  onViewProfile={setProfileFriend}
                  onPlay={(friend) => setPlayFriend(friend)}
                />
              )}
              {activeTab === 'requests' && (
                <RequestsTab
                  received={pendingReceived}
                  sent={pendingSent}
                  onAccept={acceptRequest}
                  onDecline={declineRequest}
                />
              )}
              {activeTab === 'add' && <AddFriendTab onSend={sendRequest} />}
            </div>
          </>
        )}
      </div>

      {/* Play Mode Modal */}
      {playFriend && (
        <PlayModeModal
          friend={playFriend}
          onCasual={() => handlePlayCasual(playFriend)}
          onSandbox={() => handlePlaySandbox(playFriend)}
          onClose={() => setPlayFriend(null)}
        />
      )}

      {/* Game Invite Toasts */}
      {incomingInvites.map((invite) => (
        <GameInviteToast key={invite.id} invite={invite} />
      ))}

      {/* Friend Online Toasts */}
      {onlineNotifications.map((friend, i) => (
        <FriendOnlineToast
          key={friend.uid}
          friend={friend}
          index={incomingInvites.length + i}
          onDismiss={() => dismissOnlineNotification(friend.uid)}
          onClick={() => { dismissOnlineNotification(friend.uid); openSidebar(); }}
        />
      ))}

      {/* Message Toasts */}
      {messageNotifications.map((notif, i) => (
        <MessageToast
          key={notif.friendshipId}
          notification={notif}
          index={incomingInvites.length + onlineNotifications.length + i}
          onDismiss={() => dismissMessageNotification(notif.friendshipId)}
          onClick={() => { dismissMessageNotification(notif.friendshipId); openSidebar(); openConversation(notif.friend.uid); }}
        />
      ))}

      {/* Pending Invite Toast (host waiting for response) */}
      {pendingInvite && (
        <PendingInviteToast
          friendName={pendingInvite.friendName}
          onCancel={() => {
            setPendingInvite(null);
            setIsCreatingGame(false);
            if (inviteUnsubRef.current) { inviteUnsubRef.current(); inviteUnsubRef.current = null; }
          }}
        />
      )}

      {/* Decline Toast */}
      {declineNotification && (
        <DeclineToast
          friendName={declineNotification.friendName}
          onDismiss={() => setDeclineNotification(null)}
        />
      )}

      {/* In Match Toast */}
      {inMatchNotification && (
        <InMatchToast
          friendName={inMatchNotification.friendName}
          onDismiss={() => setInMatchNotification(null)}
        />
      )}
    </>
  );
}

// ─── Friends Tab ────────────────────────────────────────────────────────────

function FriendsTab({
  friends,
  unreadByConversationId,
  onMessage,
  onViewProfile,
  onPlay,
}: {
  friends: Friend[];
  unreadByConversationId: Set<string>;
  onMessage: (uid: string) => void;
  onViewProfile: (friend: Friend) => void;
  onPlay: (friend: Friend) => void;
}) {
  if (friends.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>&#128100;</div>
        <div className={styles.emptyTitle}>No friends yet</div>
        <div className={styles.emptyText}>
          Use the Add tab to find players by their username#tag
        </div>
      </div>
    );
  }

  return (
    <>
      {friends.map((friend) => {
        const hasUnread = unreadByConversationId.has(friend.friendshipId);
        return (
          <div key={friend.uid} className={styles.friendItem}>
            <div
              className={styles.friendInfoClickable}
              onClick={() => onViewProfile(friend)}
              title="View profile"
            >
              <div className={styles.avatar}>
                {friend.zodiacResult ? (
                  <img
                    src={getPieceImage(friend.zodiacResult)}
                    alt={friend.zodiacResult}
                    className={styles.avatarImg}
                  />
                ) : '?'}
                <span className={`${styles.statusDot} ${friend.isOnline ? styles.online : styles.offline}`} />
              </div>
              <div className={styles.friendInfo}>
                <div>
                  <span className={styles.friendName}>{friend.username}</span>
                  <span className={styles.friendTag}>#{friend.friendTag}</span>
                </div>
                <div className={`${styles.friendStatus} ${friend.isOnline ? '' : styles.offline}`}>
                  {friend.isOnline ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
            <div className={styles.friendActions}>
              {friend.isOnline && (
                <button
                  className={`${styles.actionButton} ${styles.playAction}`}
                  onClick={() => onPlay(friend)}
                  title="Play together"
                >
                  Play
                </button>
              )}
              <button
                className={styles.actionButton}
                onClick={() => onMessage(friend.uid)}
                title={hasUnread ? 'New message' : 'Open chat'}
              >
                Chat
                {hasUnread && <span className={styles.chatUnreadDot} aria-label="Unread message" />}
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─── Requests Tab ───────────────────────────────────────────────────────────

function RequestsTab({
  received,
  sent,
  onAccept,
  onDecline,
}: {
  received: FriendRequest[];
  sent: FriendRequest[];
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
}) {
  if (received.length === 0 && sent.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>&#128236;</div>
        <div className={styles.emptyTitle}>No pending requests</div>
      </div>
    );
  }

  return (
    <>
      {received.length > 0 && (
        <div className={styles.requestSection}>
          <div className={styles.requestSectionTitle}>Received</div>
          {received.map((req) => (
            <div key={req.friendshipId} className={styles.friendItem}>
              <div className={styles.avatar}>
                {req.fromZodiac ? req.fromZodiac.charAt(0).toUpperCase() : '?'}
              </div>
              <div className={styles.friendInfo}>
                <div>
                  <span className={styles.friendName}>{req.fromUsername}</span>
                  <span className={styles.friendTag}>#{req.fromFriendTag}</span>
                </div>
              </div>
              <div className={styles.friendActions}>
                <button
                  className={`${styles.actionButton} ${styles.primary}`}
                  onClick={() => onAccept(req.friendshipId)}
                >
                  Accept
                </button>
                <button
                  className={`${styles.actionButton} ${styles.danger}`}
                  onClick={() => onDecline(req.friendshipId)}
                >
                  &#10005;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {sent.length > 0 && (
        <div className={styles.requestSection}>
          <div className={styles.requestSectionTitle}>Sent</div>
          {sent.map((req) => (
            <div key={req.friendshipId} className={styles.friendItem}>
              <div className={styles.avatar}>
                {req.fromZodiac ? req.fromZodiac.charAt(0).toUpperCase() : '?'}
              </div>
              <div className={styles.friendInfo}>
                <div>
                  <span className={styles.friendName}>{req.fromUsername}</span>
                  <span className={styles.friendTag}>#{req.fromFriendTag}</span>
                </div>
                <div className={`${styles.friendStatus} ${styles.offline}`}>Pending</div>
              </div>
              <button
                className={`${styles.actionButton} ${styles.danger}`}
                onClick={() => onDecline(req.friendshipId)}
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Add Friend Tab ─────────────────────────────────────────────────────────

function AddFriendTab({
  onSend,
}: {
  onSend: (usernameWithTag: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const { userProfile } = useContext(AuthContext);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message: string }>({
    type: 'idle',
    message: '',
  });

  const handleSubmit = async () => {
    if (!input.trim()) return;
    setStatus({ type: 'loading', message: 'Searching...' });

    try {
      const result = await onSend(input.trim());
      if (result.success) {
        setStatus({ type: 'success', message: 'Friend request sent!' });
        setInput('');
      } else {
        setStatus({ type: 'error', message: result.error || 'Something went wrong' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <div className={styles.addFriendSection}>
      {/* Show user's own tag for sharing */}
      {userProfile && (
        <div className={styles.yourTag}>
          <div className={styles.yourTagLabel}>Your friend tag</div>
          <div className={styles.yourTagValue}>
            {userProfile.username}#{userProfile.friendTag || '000'}
          </div>
        </div>
      )}

      <div className={styles.searchRow}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Username#000"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (status.type !== 'idle') setStatus({ type: 'idle', message: '' });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
        />
        <button
          className={styles.searchButton}
          onClick={handleSubmit}
          disabled={status.type === 'loading' || !input.trim()}
        >
          {status.type === 'loading' ? '...' : 'Add'}
        </button>
      </div>

      {status.message && (
        <div className={`${styles.searchMessage} ${status.type === 'error' ? styles.error : ''} ${status.type === 'success' ? styles.success : ''}`}>
          {status.message}
        </div>
      )}
    </div>
  );
}

// ─── Chat View ──────────────────────────────────────────────────────────────

function ChatView({
  friend,
  messages,
  onBack,
  onSend,
}: {
  friend: Friend;
  messages: { id: string; fromUid: string; text: string; timestamp: Date }[];
  onBack: () => void;
  onSend: (text: string) => void;
}) {
  const { user } = useContext(AuthContext);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
  };

  return (
    <div className={styles.chatContainer}>
      <div className={styles.chatHeader}>
        <button className={styles.chatBackButton} onClick={onBack}>
          &#8592;
        </button>
        <span className={styles.friendName}>{friend.username}</span>
        <span className={styles.friendTag}>#{friend.friendTag}</span>
      </div>

      <div className={styles.chatMessages}>
        {messages.length === 0 && (
          <div className={styles.chatEmpty}>
            Start a conversation with {friend.username}
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${styles.chatBubble} ${msg.fromUid === user?.uid ? styles.mine : styles.theirs}`}
          >
            {msg.text}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.chatInputRow}>
        <input
          className={styles.chatInput}
          type="text"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend();
          }}
        />
        <button className={styles.chatSendButton} onClick={handleSend}>
          &#9654;
        </button>
      </div>
    </div>
  );
}

// ─── Friend Profile View ─────────────────────────────────────────────────────

function FriendProfileView({
  friend,
  onBack,
  onChat,
  onRemove,
  onPlay,
}: {
  friend: Friend;
  onBack: () => void;
  onChat: (uid: string) => void;
  onRemove: (friendshipId: string) => void;
  onPlay: (friend: Friend) => void;
}) {
  const [userData, setUserData] = useState<FirebaseUserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    UserDataService.getUserData(friend.uid).then((data) => {
      if (!cancelled) {
        setUserData(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [friend.uid]);

  const profile = userData?.profile;
  const mmr = profile?.mmr;

  const getHighestRating = () => {
    if (!profile) return { rating: 1300, isPlaced: false };
    return MMRService.getHighestRating(profile);
  };

  return (
    <div className={styles.profileContainer}>
      <div className={styles.chatHeader}>
        <button className={styles.chatBackButton} onClick={onBack}>
          &#8592;
        </button>
        <span className={styles.friendName}>{friend.username}</span>
        <span className={styles.friendTag}>#{friend.friendTag}</span>
      </div>

      {loading ? (
        <div className={styles.profileLoading}>Loading profile...</div>
      ) : !profile ? (
        <div className={styles.profileLoading}>Profile not available</div>
      ) : (
        <div className={styles.profileBody}>
          {/* Avatar + Name */}
          <div className={styles.profileHeader}>
            <div className={styles.profileAvatar}>
              {profile.zodiacResult ? (
                <img
                  src={getPieceImage(profile.zodiacResult)}
                  alt={profile.zodiacResult}
                  className={styles.profileAvatarImg}
                />
              ) : (
                friend.username.charAt(0).toUpperCase()
              )}
            </div>
            <div className={styles.profileNameBlock}>
              <div className={styles.profileUsername}>{friend.username}<span className={styles.profileTag}>#{friend.friendTag}</span></div>
              {profile.zodiacResult && (
                <div className={styles.profileZodiac}>{profile.zodiacResult}</div>
              )}
              <div className={`${styles.profileOnline} ${friend.isOnline ? styles.online : styles.offline}`}>
                {friend.isOnline ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className={styles.profileStats}>
            <div className={styles.profileStatRow}>
              <span className={styles.profileStatLabel}>Rating</span>
              <span className={styles.profileStatValue}>
                {getHighestRating().isPlaced
                  ? `${getHighestRating().rating} ELO`
                  : `Placement ${mmr?.placementGames || 0}/10`
                }
              </span>
            </div>

            {mmr && mmr.isPlaced && mmr.peak > 0 && (
              <div className={styles.profileStatRow}>
                <span className={styles.profileStatLabel}>Peak</span>
                <span className={styles.profileStatValue}>
                  {Math.max(
                    mmr.peak || 0,
                    ...(mmr.timeControlRatings
                      ? (['sprint', 'standard', 'marathon'] as const).map(
                          tc => mmr.timeControlRatings?.[tc]?.peak || 0
                        )
                      : []),
                    getHighestRating().rating
                  )} ELO
                </span>
              </div>
            )}

            <div className={styles.profileStatRow}>
              <span className={styles.profileStatLabel}>Games</span>
              <span className={styles.profileStatValue}>{mmr?.totalGames || 0}</span>
            </div>

            {userData?.createdAt && (
              <div className={styles.profileStatRow}>
                <span className={styles.profileStatLabel}>Member since</span>
                <span className={styles.profileStatValue}>
                  {(() => {
                    const raw = userData.createdAt as any;
                    const d = raw?.toDate ? raw.toDate() : raw instanceof Date ? raw : new Date(raw);
                    return isNaN(d.getTime()) ? 'Unknown' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                  })()}
                </span>
              </div>
            )}
          </div>

          {/* Time Control Ratings */}
          {mmr?.timeControlRatings && (
            <div className={styles.profileRatings}>
              <div className={styles.profileSectionTitle}>Ratings by Time Control</div>
              <div className={styles.profileRatingsGrid}>
                {(['sprint', 'standard', 'marathon'] as TimeControlType[]).map(tc => {
                  const tcRating = mmr.timeControlRatings?.[tc];
                  return (
                    <TimeControlBadge
                      key={tc}
                      timeControl={tc}
                      rating={tcRating ? (tcRating.isPlaced ? tcRating.rating : tcRating.provisionalRating) : 1300}
                      games={tcRating?.games || 0}
                      isPlaced={tcRating?.isPlaced || false}
                      placementGames={tcRating?.placementGames || 0}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Play button (only when online) */}
          {friend.isOnline && (
            <button
              className={styles.profilePlayButton}
              onClick={() => onPlay(friend)}
            >
              Play Together
            </button>
          )}

          {/* Chat button */}
          <button
            className={styles.profileChatButton}
            onClick={() => onChat(friend.uid)}
          >
            Send Message
          </button>

          {/* Remove friend */}
          <button
            className={styles.profileRemoveButton}
            onClick={() => setShowRemoveConfirm(true)}
          >
            Remove Friend
          </button>

          {/* Remove confirmation modal */}
          {showRemoveConfirm && (
            <div className={styles.confirmOverlay} onClick={() => setShowRemoveConfirm(false)}>
              <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.confirmTitle}>Are you sure?</div>
                <div className={styles.confirmText}>
                  You will be removing <strong>{friend.username}#{friend.friendTag}</strong> from your friends list.
                </div>
                <div className={styles.confirmActions}>
                  <button
                    className={styles.confirmRemoveBtn}
                    onClick={() => onRemove(friend.friendshipId)}
                  >
                    Remove
                  </button>
                  <button
                    className={styles.confirmCancelBtn}
                    onClick={() => setShowRemoveConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Play Mode Modal ─────────────────────────────────────────────────────────

function PlayModeModal({
  friend,
  onCasual,
  onSandbox,
  onClose,
}: {
  friend: Friend;
  onCasual: () => void;
  onSandbox: () => void;
  onClose: () => void;
}) {
  return (
    <div className={styles.confirmOverlay} onClick={onClose}>
      <div className={styles.playModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.playModalTitle}>Play with {friend.username}</div>
        <div className={styles.playModalOptions}>
          <button className={styles.playOptionCasual} onClick={onCasual}>
            <div className={styles.playOptionIcon}>&#9876;</div>
            <div className={styles.playOptionLabel}>Casual</div>
            <div className={styles.playOptionDesc}>No ELO — just for fun</div>
          </button>
          <button className={styles.playOptionSandbox} onClick={onSandbox}>
            <div className={styles.playOptionIcon}>&#9881;</div>
            <div className={styles.playOptionLabel}>Sandbox</div>
            <div className={styles.playOptionDesc}>Shared board — drag and drop together</div>
          </button>
        </div>
        <button className={styles.playModalCancel} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Game Invite Toast ──────────────────────────────────────────────────────

function GameInviteToast({ invite }: { invite: GameInvite }) {
  const { acceptGameInvite, declineGameInvite } = useContext(FriendsContext);
  const [accepting, setAccepting] = useState(false);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const gameId = await acceptGameInvite(invite.id);
      // Route sandbox invites to shared sandbox, others to multiplayer
      if (invite.timeControl === 'sandbox') {
        window.location.href = `/shared-sandbox/${gameId}`;
      } else {
        window.location.href = `/multiplayer?join=${gameId}`;
      }
    } catch {
      setAccepting(false);
    }
  };

  const getInviteLabel = () => {
    if (invite.timeControl === 'sandbox') return 'Sandbox session';
    if (invite.timeControl === 'casual') return 'Casual game';
    return `${invite.timeControl} game`;
  };

  return (
    <div className={styles.inviteToast}>
      <div className={styles.inviteTitle}>
        {invite.fromUsername}#{invite.fromFriendTag} wants to play!
      </div>
      <div className={styles.inviteSubtext}>
        {getInviteLabel()}
      </div>
      <div className={styles.inviteActions}>
        <button
          className={`${styles.actionButton} ${styles.primary}`}
          onClick={handleAccept}
          disabled={accepting}
        >
          {accepting ? 'Joining...' : 'Accept'}
        </button>
        <button
          className={styles.actionButton}
          onClick={() => declineGameInvite(invite.id)}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

// ─── Notification Sound ─────────────────────────────────────────────────────

const notificationAudio = typeof window !== 'undefined' ? new Audio('/beep-313342.mp3') : null;
if (notificationAudio) notificationAudio.volume = 0.3;

function playNotificationSound() {
  if (!notificationAudio) return;
  notificationAudio.currentTime = 0;
  notificationAudio.play().catch(() => {});
}

// ─── Friend Online Toast ────────────────────────────────────────────────────

function FriendOnlineToast({
  friend,
  index,
  onDismiss,
  onClick,
}: {
  friend: Friend;
  index: number;
  onDismiss: () => void;
  onClick: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`${styles.notifToast} ${styles.notifOnline}`}
      style={{ top: `${20 + index * 70}px` }}
      onClick={onClick}
    >
      <div className={styles.notifDot} />
      <div className={styles.notifBody}>
        <div className={styles.notifTitle}>{friend.username} is online</div>
      </div>
      <button
        className={styles.notifClose}
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
      >
        &#10005;
      </button>
    </div>
  );
}

// ─── Message Toast ──────────────────────────────────────────────────────────

function MessageToast({
  notification,
  index,
  onDismiss,
  onClick,
}: {
  notification: MessageNotification;
  index: number;
  onDismiss: () => void;
  onClick: () => void;
}) {
  useEffect(() => {
    playNotificationSound();
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`${styles.notifToast} ${styles.notifMessage}`}
      style={{ top: `${20 + index * 70}px` }}
      onClick={onClick}
    >
      <div className={styles.notifIcon}>&#9993;</div>
      <div className={styles.notifBody}>
        <div className={styles.notifTitle}>{notification.friend.username}</div>
        <div className={styles.notifPreview}>{notification.preview}</div>
      </div>
      <button
        className={styles.notifClose}
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
      >
        &#10005;
      </button>
    </div>
  );
}

// ─── Pending Invite Toast (host waiting for response) ─────────────────────

function PendingInviteToast({
  friendName,
  onCancel,
}: {
  friendName: string;
  onCancel: () => void;
}) {
  return (
    <div className={`${styles.notifToast} ${styles.notifPending}`} style={{ top: '20px' }}>
      <div className={styles.notifIcon}>&#9203;</div>
      <div className={styles.notifBody}>
        <div className={styles.notifTitle}>Invite sent to {friendName}</div>
        <div className={styles.notifPreview}>Waiting for response...</div>
      </div>
      <button
        className={styles.notifClose}
        onClick={onCancel}
        title="Cancel invite"
      >
        &#10005;
      </button>
    </div>
  );
}

// ─── Decline Toast ────────────────────────────────────────────────────────

function DeclineToast({
  friendName,
  onDismiss,
}: {
  friendName: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`${styles.notifToast} ${styles.notifDecline}`} style={{ top: '20px' }}>
      <div className={styles.notifIcon}>&#10007;</div>
      <div className={styles.notifBody}>
        <div className={styles.notifTitle}>{friendName} declined your invite</div>
      </div>
      <button
        className={styles.notifClose}
        onClick={onDismiss}
      >
        &#10005;
      </button>
    </div>
  );
}

// ─── In Match Toast ──────────────────────────────────────────────────────

function InMatchToast({
  friendName,
  onDismiss,
}: {
  friendName: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`${styles.notifToast} ${styles.notifDecline}`} style={{ top: '20px' }}>
      <div className={styles.notifIcon}>&#9888;</div>
      <div className={styles.notifBody}>
        <div className={styles.notifTitle}>{friendName} is currently in a match</div>
        <div className={styles.notifPreview}>Try again when they're available</div>
      </div>
      <button
        className={styles.notifClose}
        onClick={onDismiss}
      >
        &#10005;
      </button>
    </div>
  );
}
